import React, { forwardRef, useId, useState } from 'react';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import { FieldErrorMessage } from '../FieldErrorMessage';
import { TextFieldProps } from '../../types';

export const ShadCNTextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (props, ref) => {
    const {
      name,
      label,
      value = '',
      placeholder,
      type = 'text',
      onChange,
      onBlur,
      onFocus,
      error,
      helperText,
      isDisabled = false,
      isRequired = false,
      success = false,
      leftElement,
      rightElement,
      className,
      inputClassName,
      labelClassName,
      maxLength,
      pattern,
      autoComplete = 'off',
      autoFocus = false,
      showErrorWhenFocused = false
    } = props;

    const inputId = useId();
    const [isFocusWithin, setIsFocusWithin] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocusWithin(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocusWithin(false);
      onBlur?.(e);
    };

    const hasError = Boolean(error);
    const hasSuccess = success && !hasError;

    // Error visibility: hide when focused (match Omega pattern), unless showErrorWhenFocused
    const showError =
      hasError && (showErrorWhenFocused || !isFocusWithin);

    // Helper text: show when no error OR when focused (if helper text exists)
    const showHelperText =
      (!hasError || isFocusWithin) && !hasSuccess && helperText;

    return (
      <div className={cn(DEFAULT_STYLES.field, className, 'base__text__field')}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              DEFAULT_STYLES.field__label,
              isDisabled && DEFAULT_STYLES.field__labelDisabled,
              labelClassName
            )}
          >
            {label}
            {isRequired && (
              <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
            )}
          </label>
        )}

        {/* Container with border - left/right elements INSIDE the same container */}
        <div
          className={cn(
            DEFAULT_STYLES.field__inputContainer,
            showError
              ? 'border-errorRed'
              : hasSuccess
                ? DEFAULT_STYLES.field__inputContainerSuccess
                : DEFAULT_STYLES.field__inputContainerDefault,
            isFocusWithin &&
            !showError &&
            !hasSuccess &&
            !isDisabled &&
            'border-lightIndigo600 ring-1 ring-lightIndigo600 input-container-focused',
            isDisabled && DEFAULT_STYLES.field__inputContainerDisabled
          )}
        >
          {/* Left element - static, inside container */}
          {leftElement && (
            <div className={DEFAULT_STYLES.field__sideElement}>
              {leftElement}
            </div>
          )}

          {/* Input - no border, transparent background */}
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={isDisabled}
            maxLength={maxLength}
            pattern={pattern}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : undefined}
            className={cn(
              DEFAULT_STYLES.field__input,
              isDisabled
                ? DEFAULT_STYLES.field__inputDisabled
                : DEFAULT_STYLES.field__inputText,
              DEFAULT_STYLES.field__inputPlaceholder,
              inputClassName
            )}
            style={isDisabled ? { WebkitTextFillColor: '#94a3b8' } : undefined}
          />

          {/* Right element - static, inside container */}
          {rightElement && (
            <div className={DEFAULT_STYLES.field__sideElement}>
              {rightElement}
            </div>
          )}
        </div>

        {/* Single feedback container - always rendered to prevent layout shift */}
        {(hasError || showHelperText) &&
          <p
            id={`${inputId}-error`}
            className={cn(
              DEFAULT_STYLES.field__message,
              showError
                ? 'text-errorTextColor'
                : hasSuccess && helperText
                  ? 'text-successTextColor'
                  : showHelperText
                    ? 'text-helperTextColor'
                    : 'invisible h-4'
            )}
            role={showError ? 'alert' : undefined}
          >
            {showError && error
              ? <FieldErrorMessage message={error} />
              : hasSuccess && helperText
                ? helperText
                : showHelperText
                  ? helperText
                  : ''}
          </p>
        }
      </div>
    );
  }
);

ShadCNTextField.displayName = 'TextField';
